import { useState, useEffect, useCallback } from "react";
import {
  Box, Typography, Chip, ToggleButton, ToggleButtonGroup, Select, MenuItem,
  FormControl, InputLabel, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, CircularProgress, Alert, IconButton,
  Tooltip, Divider, SwipeableDrawer,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import LocalPoliceIcon from "@mui/icons-material/LocalPolice";
import LocalFireDepartmentIcon from "@mui/icons-material/LocalFireDepartment";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import MapIcon from "@mui/icons-material/Map";
import ListIcon from "@mui/icons-material/List";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import LightModeIcon from "@mui/icons-material/LightMode";
import HistoryIcon from "@mui/icons-material/History";
import TuneIcon from "@mui/icons-material/Tune";
import CloseIcon from "@mui/icons-material/Close";
import { Badge } from "@mui/material";
import { MapContainer, TileLayer, CircleMarker, Popup, GeoJSON, useMap } from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-cluster";
import "leaflet/dist/leaflet.css";

import { upsertMajorEvents, fetchHistoricalMajors } from "../supabase";
import {
  HOURS_OPTIONS, DAYS_OPTIONS, POLICE_DIVISIONS, DIVISION_LABELS,
  DIVISION_COORDS, TPS_URL, TFS_PROXY, DIVISIONS_GEOJSON_URL,
} from "../constants";
import { alarmColor, fmtTime, fmtDate, cleanType, isMajorPolice, isMajorFire } from "../helpers";

// ─── Data fetchers ────────────────────────────────────────────────────────────
async function fetchPolice(hours, division) {
  const since = new Date(Date.now() - hours * 60 * 60 * 1000).toISOString().replace("T", " ").replace("Z", "");
  let where = `OCCURRENCE_TIME >= timestamp '${since}'`;
  if (division !== "All") where += ` AND DIVISION = '${division}'`;
  const params = new URLSearchParams({ f: "json", where, outFields: "OBJECTID,OCCURRENCE_TIME,DIVISION,CALL_TYPE,CROSS_STREETS,LATITUDE,LONGITUDE", orderByFields: "OCCURRENCE_TIME DESC", resultRecordCount: 500 });
  const res = await fetch(`${TPS_URL}?${params}`);
  const json = await res.json();
  if (!json.features) return [];
  return json.features.map(f => ({
    id: f.attributes.OBJECTID, time: f.attributes.OCCURRENCE_TIME,
    division: f.attributes.DIVISION, type: f.attributes.CALL_TYPE,
    location: f.attributes.CROSS_STREETS, lat: f.attributes.LATITUDE, lng: f.attributes.LONGITUDE,
    major: isMajorPolice(f.attributes.CALL_TYPE), _type: "police",
  }));
}

function parseFireHTML(html) {
  const doc = new DOMParser().parseFromString(html, "text/html");
  const incidents = [];
  doc.querySelectorAll("table tr").forEach((row, i) => {
    if (i === 0) return;
    const c = row.querySelectorAll("td");
    if (c.length < 6) return;
    const alarmLevel = c[5]?.textContent?.trim();
    incidents.push({
      id: `fire-${i}`, rawTime: c[2]?.textContent?.trim(),
      type: c[4]?.textContent?.trim() || "Fire Incident", alarmLevel,
      area: c[6]?.textContent?.trim(), units: c[7]?.textContent?.trim(),
      location: `${c[0]?.textContent?.trim()} & ${c[1]?.textContent?.trim()}`,
      major: isMajorFire(alarmLevel), _type: "fire",
    });
  });
  return incidents;
}

async function fetchFire() {
  try { return parseFireHTML(await (await fetch(TFS_PROXY)).text()); }
  catch { return []; }
}

// ─── Map components ───────────────────────────────────────────────────────────
function MapController({ center, zoom, divisionFilter }) {
  const map = useMap();
  useEffect(() => {
    map.flyTo(center, zoom, { animate: true, duration: 0.6 });
  }, [center, zoom, map, divisionFilter]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

function FlyToIncident({ incident }) {
  const map = useMap();
  useEffect(() => {
    if (!incident?.lat || !incident?.lng) return;
    map.flyTo([incident.lat, incident.lng], 15, { animate: true, duration: 0.5 });
    const timer = setTimeout(() => {
      map.eachLayer(layer => {
        if (layer.getLatLng) {
          const ll = layer.getLatLng();
          if (Math.abs(ll.lat - incident.lat) < 0.0001 && Math.abs(ll.lng - incident.lng) < 0.0001) {
            layer.openPopup();
          }
        }
      });
    }, 600);
    return () => clearTimeout(timer);
  }, [incident, map]);
  return null;
}

function DivisionLabels({ geojson, dark, selectedDiv }) {
  const map = useMap();
  useEffect(() => {
    if (!geojson || !map) return;
    const labels = [];
    geojson.features.forEach(f => {
      const div = f.properties.DIV;
      const coords = f.geometry.coordinates;
      const type = f.geometry.type;
      let ring = type === "Polygon" ? coords[0] : coords[0][0];
      const lng = ring.reduce((s, c) => s + c[0], 0) / ring.length;
      const lat = ring.reduce((s, c) => s + c[1], 0) / ring.length;
      const isSelected = selectedDiv !== "All" && selectedDiv === div;
      const L = window.L;
      const label = L.divIcon({
        className: "",
        html: `<div style="font-family:'Outfit',sans-serif;font-size:${isSelected ? "13px" : "11px"};font-weight:${isSelected ? "800" : "600"};color:${isSelected ? "#3b82f6" : (dark ? "#94a3b8" : "#475569")};background:${dark ? "rgba(10,14,26,0.75)" : "rgba(255,255,255,0.75)"};padding:2px 5px;border-radius:4px;white-space:nowrap;pointer-events:none;backdrop-filter:blur(4px);">${div}</div>`,
        iconAnchor: [20, 10],
      });
      const marker = L.marker([lat, lng], { icon: label, interactive: false, zIndexOffset: 100 });
      marker.addTo(map);
      labels.push(marker);
    });
    return () => { labels.forEach(l => l.remove()); };
  }, [geojson, map, dark, selectedDiv]);
  return null;
}

function CreatePane({ name, zIndex }) {
  const map = useMap();
  useEffect(() => {
    if (!map.getPane(name)) {
      const pane = map.createPane(name);
      pane.style.zIndex = zIndex;
    }
  }, [map, name, zIndex]);
  return null;
}

// CARTO now requires a (free) API key for basemap tile requests, or every
// tile renders with an "API KEY REQUIRED" watermark. Get one at
// https://carto.com/basemaps/apikey/ (no signup, ~1 min, 5M requests/month
// free) and set REACT_APP_CARTO_API_KEY in .env.local (and in Vercel's
// project env vars for production).
const CARTO_KEY = process.env.REACT_APP_CARTO_API_KEY;
const cartoKeyParam = CARTO_KEY ? `?key=${CARTO_KEY}` : "";

function IncidentMap({ policeData, fireData, divisionFilter, colorMode, onDivisionClick, historicalData, isHistorical, selectedIncident, border }) {
  const dark = colorMode === "dark";
  const [divisionsGeo, setDivisionsGeo] = useState(null);
  const coords = divisionFilter !== "All" && DIVISION_COORDS[divisionFilter] ? DIVISION_COORDS[divisionFilter] : [43.6920, -79.2950];
  const zoom = divisionFilter !== "All" ? 13 : 11;
  const tileUrl = `https://basemaps.cartocdn.com/rastertiles/${dark ? "dark_all" : "light_all"}/{z}/{x}/{y}{r}.png${cartoKeyParam}`;
  const liveMarkers = [...policeData.filter(r => r.lat && r.lng), ...fireData.filter(r => r.lat && r.lng)];
  const histMarkers = (historicalData || []).filter(r => r.lat && r.lng);

  useEffect(() => {
    fetch(DIVISIONS_GEOJSON_URL)
      .then(r => r.json())
      .then(data => { if (data.features) setDivisionsGeo(data); })
      .catch(() => {});
  }, []);

  const divisionStyle = (feature) => {
    const isSelected = divisionFilter !== "All" && feature.properties.DIV === divisionFilter;
    return {
      color: isSelected ? "#3b82f6" : (dark ? "#475569" : "#94a3b8"),
      weight: isSelected ? 2.5 : 1,
      fillColor: isSelected ? "#3b82f6" : (dark ? "#ffffff" : "#000000"),
      fillOpacity: isSelected ? 0.08 : 0.03,
      dashArray: isSelected ? null : "4 4",
    };
  };

  return (
    <MapContainer center={coords} zoom={zoom} style={{ width: "100%", height: "100%" }} zoomControl>
      <TileLayer url={tileUrl} attribution='&copy; <a href="https://carto.com/">CARTO</a>' />
      {dark && (
        <TileLayer
          url={`https://basemaps.cartocdn.com/rastertiles/dark_only_labels/{z}/{x}/{y}{r}.png${cartoKeyParam}`}
          opacity={0.85}
          pane="shadowPane"
        />
      )}
      <MapController center={coords} zoom={zoom} divisionFilter={divisionFilter} />
      <CreatePane name="markerPane" zIndex={450} />
      <FlyToIncident incident={selectedIncident} />
      {divisionsGeo && (
        <>
          <GeoJSON
            key={`${divisionFilter}-${dark}`}
            data={divisionsGeo}
            style={divisionStyle}
            onEachFeature={(feature, layer) => {
              const div = feature.properties.DIV;
              layer.on({
                click: () => onDivisionClick && onDivisionClick(div),
                mouseover: (e) => { e.target.setStyle({ fillOpacity: 0.15, weight: 2 }); },
                mouseout: (e) => { e.target.setStyle(divisionStyle(feature)); },
              });
              layer.bindTooltip(div, { permanent: false, direction: "center", className: "division-tooltip" });
            }}
          />
          <DivisionLabels geojson={divisionsGeo} dark={dark} selectedDiv={divisionFilter} />
        </>
      )}

      {isHistorical && histMarkers.length > 0 && (
        <MarkerClusterGroup
          chunkedLoading
          maxClusterRadius={50}
          iconCreateFunction={(cluster) => {
            const count = cluster.getChildCount();
            const size = count > 50 ? 44 : count > 20 ? 36 : 28;
            const bg = count > 50 ? "#7f1d1d" : count > 20 ? "#991b1b" : "#b91c1c";
            return window.L.divIcon({
              html: `<div style="width:${size}px;height:${size}px;border-radius:50%;background:${bg};border:2px solid #fca5a5;display:flex;align-items:center;justify-content:center;color:#fff;font-weight:800;font-size:${size > 36 ? 13 : 11}px;font-family:'Outfit',sans-serif;box-shadow:0 2px 8px rgba(0,0,0,0.4);">${count}</div>`,
              className: "", iconSize: [size, size], iconAnchor: [size / 2, size / 2],
            });
          }}
        >
          {histMarkers.map(row => {
            const isPolice = row._type === "police";
            const color = isPolice ? "#f59e0b" : "#f97316";
            const dateStr = row.time ? new Date(row.time).toLocaleDateString("en-CA", { weekday: "short", month: "short", day: "numeric" }) : "";
            const timeStr = row.time ? new Date(row.time).toLocaleTimeString("en-CA", { hour: "2-digit", minute: "2-digit", hour12: false }) : "";
            return (
              <CircleMarker key={`hist-${row.id}`} center={[row.lat, row.lng]} radius={11}
                pane="markerPane" bubblingMouseEvents={false}
                pathOptions={{ color, fillColor: color, fillOpacity: 0.9, weight: 2 }}>
                <Popup autoPan autoPanPadding={[20, 20]} minWidth={200}>
                  <div style={{ fontFamily: "'Outfit', sans-serif", padding: "2px 0" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
                      <div style={{ width: 8, height: 8, borderRadius: "50%", background: color, flexShrink: 0 }} />
                      <div style={{ fontWeight: 700, fontSize: "0.9rem", textTransform: "capitalize", color: "#0f172a" }}>
                        {isPolice ? cleanType(row.type)?.toLowerCase().replace(/_/g, ' ') : cleanType(row.type)}
                      </div>
                    </div>
                    {row.location && <div style={{ fontSize: "0.78rem", color: "#475569", marginBottom: 4, lineHeight: 1.4 }}>📍 {row.location}</div>}
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 6 }}>
                      {row.division && <span style={{ fontSize: "0.72rem", background: "#eff6ff", color: "#1d4ed8", padding: "2px 7px", borderRadius: 4, fontWeight: 700 }}>{row.division}</span>}
                      {!isPolice && row.alarmLevel && <span style={{ fontSize: "0.72rem", background: "#fff7ed", color: "#c2410c", padding: "2px 7px", borderRadius: 4, fontWeight: 700 }}>Alarm {row.alarmLevel}</span>}
                    </div>
                    {dateStr && <div style={{ fontSize: "0.72rem", color: "#94a3b8" }}>🕐 {dateStr}{timeStr ? ` · ${timeStr}` : ""}</div>}
                    <div style={{ marginTop: 7, padding: "3px 8px", background: "#fef3c7", borderRadius: 4, display: "inline-block", fontSize: "0.7rem", color: "#92400e", fontWeight: 700 }}>⚠ Major Incident</div>
                  </div>
                </Popup>
              </CircleMarker>
            );
          })}
        </MarkerClusterGroup>
      )}

      {!isHistorical && liveMarkers.map(row => {
        const isPolice = row._type === "police";
        const color = isPolice ? (row.major ? "#f59e0b" : "#3b82f6") : (row.major ? "#f97316" : "#ef4444");
        const baseRadius = row.major ? 13 : 10;
        const timeLabel = isPolice ? fmtTime(row.time) : row.rawTime;
        return (
          <CircleMarker key={row.id} center={[row.lat, row.lng]} radius={baseRadius}
            pane="markerPane" bubblingMouseEvents={false}
            pathOptions={{ color, fillColor: color, fillOpacity: 0.85, weight: row.major ? 2.5 : 1.5 }}>
            <Popup autoPan autoPanPadding={[20, 20]} minWidth={200}>
              <div style={{ fontFamily: "'Outfit', sans-serif", padding: "2px 0" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
                  <div style={{ width: 8, height: 8, borderRadius: "50%", background: color, flexShrink: 0 }} />
                  <div style={{ fontWeight: 700, fontSize: "0.9rem", textTransform: "capitalize", color: "#0f172a" }}>
                    {isPolice ? cleanType(row.type)?.toLowerCase().replace(/_/g, ' ') : cleanType(row.type)}
                  </div>
                </div>
                {row.location && <div style={{ fontSize: "0.78rem", color: "#475569", marginBottom: 4, lineHeight: 1.4 }}>📍 {row.location}</div>}
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 6 }}>
                  {isPolice && row.division && <span style={{ fontSize: "0.72rem", background: "#eff6ff", color: "#1d4ed8", padding: "2px 7px", borderRadius: 4, fontWeight: 700 }}>{row.division}</span>}
                  {!isPolice && row.alarmLevel && <span style={{ fontSize: "0.72rem", background: "#fff7ed", color: "#c2410c", padding: "2px 7px", borderRadius: 4, fontWeight: 700 }}>Alarm {row.alarmLevel}</span>}
                  {!isPolice && row.area && <span style={{ fontSize: "0.72rem", color: "#64748b" }}>{row.area}</span>}
                </div>
                {timeLabel && <div style={{ fontSize: "0.72rem", color: "#94a3b8" }}>🕐 {timeLabel}</div>}
                {!isPolice && row.units && <div style={{ fontSize: "0.72rem", color: "#94a3b8", marginTop: 2 }}>🚒 {row.units.length > 60 ? row.units.slice(0, 60) + "…" : row.units}</div>}
                {row.major && <div style={{ marginTop: 7, padding: "3px 8px", background: "#fef3c7", borderRadius: 4, display: "inline-block", fontSize: "0.7rem", color: "#92400e", fontWeight: 700 }}>⚠ Major Incident</div>}
              </div>
            </Popup>
          </CircleMarker>
        );
      })}
    </MapContainer>
  );
}

// ─── Mobile Card ──────────────────────────────────────────────────────────────
function IncidentCard({ row, dark, index, onSelect }) {
  const [expanded, setExpanded] = useState(false);
  const isPolice = row._type === "police";
  const isMaj = row.major;
  const accentColor = isMaj ? (isPolice ? "#f59e0b" : "#f97316") : (isPolice ? "#3b82f6" : "#ef4444");
  const ac = alarmColor(row.alarmLevel);

  return (
    <Box onClick={() => { if (onSelect) { onSelect(); } else { setExpanded(e => !e); } }} sx={{
      px: 2, py: 1.25,
      borderBottom: `1px solid ${dark ? "#1e293b" : "#f1f5f9"}`,
      borderLeft: `3px solid ${isMaj ? accentColor : "transparent"}`,
      bgcolor: isMaj
        ? (dark ? "#1e1a0a22" : (isPolice ? "#fffbeb" : "#fff7ed"))
        : dark
          ? (index % 2 === 0 ? "transparent" : "#ffffff08")
          : (index % 2 === 0 ? "#ffffff" : "#f8fafc"),
      cursor: "pointer", userSelect: "none",
    }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        {isMaj
          ? <WarningAmberIcon sx={{ fontSize: 15, color: accentColor, flexShrink: 0 }} />
          : isPolice
            ? <LocalPoliceIcon sx={{ fontSize: 15, color: "#3b82f6", flexShrink: 0 }} />
            : <LocalFireDepartmentIcon sx={{ fontSize: 15, color: "#ef4444", flexShrink: 0 }} />}
        <Typography sx={{ fontSize: "0.78rem", fontWeight: 500, color: "text.secondary", whiteSpace: "nowrap", flexShrink: 0, minWidth: 36 }}>
          {isPolice ? fmtTime(row.time) : (row.rawTime || "—")}
        </Typography>
        <Typography sx={{ fontSize: "0.875rem", fontWeight: isMaj ? 700 : 600, color: isMaj ? accentColor : "text.primary", textTransform: isPolice ? "capitalize" : "none", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {isPolice ? cleanType(row.type)?.toLowerCase().replace(/_/g, " ") : cleanType(row.type)}
        </Typography>
        {isPolice
          ? <Chip label={row.division} size="small" sx={{ fontSize: "0.65rem", height: 19, bgcolor: dark ? "#1e3a5f" : "#eff6ff", color: dark ? "#93c5fd" : "#1d4ed8", fontWeight: 700, flexShrink: 0 }} />
          : <Chip label={`A${row.alarmLevel}`} size="small" sx={{ fontSize: "0.65rem", height: 19, bgcolor: ac.bg, color: ac.text, fontWeight: 700, flexShrink: 0 }} />}
      </Box>
      <Box sx={{ mt: 0.5, pl: "23px" }}>
        <Typography sx={{ fontSize: "0.78rem", color: "text.secondary", lineHeight: 1.4 }}>{row.location}</Typography>
      </Box>
      {expanded && (
        <Box sx={{ mt: 0.75, pl: "23px", display: "flex", flexDirection: "column", gap: 0.25 }}>
          {isPolice && fmtDate(row.time) && <Typography sx={{ fontSize: "0.75rem", color: "text.secondary" }}>📅 {fmtDate(row.time)}</Typography>}
          {!isPolice && row.units && <Typography sx={{ fontSize: "0.75rem", color: "text.secondary" }}>🚒 {row.units}</Typography>}
          {!isPolice && row.area && <Typography sx={{ fontSize: "0.75rem", color: "text.secondary" }}>📍 {row.area}</Typography>}
          {isMaj && <Chip icon={<WarningAmberIcon sx={{ fontSize: "12px !important" }} />} label="Major Incident" size="small"
            sx={{ alignSelf: "flex-start", mt: 0.5, bgcolor: "#fef3c7", color: "#92400e", fontWeight: 700, fontSize: "0.7rem", height: 20 }} />}
        </Box>
      )}
    </Box>
  );
}

// ─── Desktop Table rows ───────────────────────────────────────────────────────
function PoliceRow({ row, dark, isHistorical, onSelect }) {
  return (
    <TableRow onClick={onSelect || undefined} sx={{
      ...(row.major ? { borderLeft: "3px solid #f59e0b", bgcolor: dark ? "#1e3a1022" : "#fffbeb" } : {}),
      ...(onSelect ? { cursor: "pointer" } : {}),
    }}>
      <TableCell sx={{ width: 20, pr: 0, pl: 1.5 }}>
        {row.major ? <WarningAmberIcon sx={{ fontSize: 15, color: "#f59e0b", verticalAlign: "middle" }} /> : <LocalPoliceIcon sx={{ fontSize: 15, color: "#3b82f6", verticalAlign: "middle" }} />}
      </TableCell>
      <TableCell sx={{ whiteSpace: "nowrap", pl: 1 }}>
        {isHistorical ? (
          <Box sx={{ fontSize: "0.8rem", fontWeight: 500 }}>{row.time ? new Date(row.time).toLocaleDateString("en-CA", { month: "short", day: "numeric" }) : "—"}</Box>
        ) : (
          <>
            <Box sx={{ fontSize: "0.875rem", fontWeight: 500 }}>{fmtTime(row.time)}</Box>
            {fmtDate(row.time) && <Box sx={{ fontSize: "0.75rem", color: "text.secondary" }}>{fmtDate(row.time)}</Box>}
          </>
        )}
      </TableCell>
      <TableCell>
        <Typography variant="body2" sx={{ fontWeight: row.major ? 700 : 600, color: row.major ? "#f59e0b" : "text.primary", textTransform: "capitalize", whiteSpace: "nowrap" }}>
          {cleanType(row.type)?.toLowerCase().replace(/_/g, " ")}
        </Typography>
        {isHistorical && <Chip label="Historical" size="small" sx={{ fontSize: "0.6rem", height: 16, ml: 0.5, bgcolor: dark ? "#1e3a5f" : "#eff6ff", color: dark ? "#93c5fd" : "#1d4ed8" }} />}
      </TableCell>
      <TableCell>
        <Typography variant="body2" sx={{ color: "text.secondary", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 260 }}>{row.location}</Typography>
      </TableCell>
      <TableCell sx={{ whiteSpace: "nowrap" }}>
        <Chip label={row.division} size="small" sx={{ fontSize: "0.72rem", height: 22, bgcolor: dark ? "#1e3a5f" : "#eff6ff", color: dark ? "#93c5fd" : "#1d4ed8", fontWeight: 700 }} />
      </TableCell>
    </TableRow>
  );
}

function FireRow({ row, dark, isHistorical, onSelect }) {
  const colors = alarmColor(row.alarmLevel);
  return (
    <TableRow onClick={onSelect || undefined} sx={{
      ...(row.major ? { borderLeft: "3px solid #f97316", bgcolor: dark ? "#3b150a22" : "#fff7ed" } : {}),
      ...(onSelect ? { cursor: "pointer" } : {}),
    }}>
      <TableCell sx={{ width: 20, pr: 0, pl: 1.5 }}>
        {row.major ? <WarningAmberIcon sx={{ fontSize: 15, color: "#f97316", verticalAlign: "middle" }} /> : <LocalFireDepartmentIcon sx={{ fontSize: 15, color: "#ef4444", verticalAlign: "middle" }} />}
      </TableCell>
      <TableCell sx={{ whiteSpace: "nowrap", pl: 1 }}>
        {isHistorical ? (
          <Box sx={{ fontSize: "0.8rem", fontWeight: 500 }}>{row.time ? new Date(row.time).toLocaleDateString("en-CA", { month: "short", day: "numeric" }) : "—"}</Box>
        ) : (
          <Box sx={{ fontSize: "0.875rem", fontWeight: 500 }}>{row.rawTime || "—"}</Box>
        )}
      </TableCell>
      <TableCell>
        <Typography variant="body2" sx={{ fontWeight: row.major ? 700 : 600, color: row.major ? "#f97316" : "text.primary", whiteSpace: "nowrap" }}>
          {cleanType(row.type)}
        </Typography>
        {!isHistorical && row.units && <Typography variant="caption" sx={{ color: "text.secondary", display: "block", fontSize: "0.72rem" }}>{row.units.length > 50 ? row.units.slice(0, 50) + "…" : row.units}</Typography>}
        {isHistorical && <Chip label="Historical" size="small" sx={{ fontSize: "0.6rem", height: 16, ml: 0.5, bgcolor: dark ? "#431407" : "#fff7ed", color: dark ? "#fb923c" : "#9a3412" }} />}
      </TableCell>
      <TableCell>
        <Typography variant="body2" sx={{ color: "text.secondary", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 260 }}>{row.location}</Typography>
        {!isHistorical && row.area && <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>{row.area}</Typography>}
      </TableCell>
      <TableCell sx={{ whiteSpace: "nowrap" }}>
        <Chip label={`Alarm ${row.alarmLevel}`} size="small" sx={{ fontSize: "0.72rem", height: 22, bgcolor: colors.bg, color: colors.text, fontWeight: 700 }} />
      </TableCell>
    </TableRow>
  );
}

// ─── DispatchPage ─────────────────────────────────────────────────────────────
export default function DispatchPage({ colorMode, setColorMode, dark, border, barBg, isMobile, defaultDivision = "D55" }) {
  const [viewMode, setViewMode] = useState("list");
  const [hoursFilter, setHoursFilter] = useState(3);
  const [daysFilter, setDaysFilter] = useState(null);
  const [divisionFilter, setDivisionFilter] = useState(defaultDivision);
  const [policeData, setPoliceData] = useState([]);
  const [fireData, setFireData] = useState([]);
  const [historicalData, setHistoricalData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [selectedIncident, setSelectedIncident] = useState(null);

  const isHistorical = daysFilter !== null;

  const activeFilterCount = [isHistorical || hoursFilter !== 3, divisionFilter !== "All"].filter(Boolean).length;

  const loadHistorical = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const data = await fetchHistoricalMajors(daysFilter, divisionFilter);
      setHistoricalData(data); setLastRefresh(new Date());
    } catch { setError("Failed to load historical data."); }
    finally { setLoading(false); }
  }, [daysFilter, divisionFilter]);

  const load = useCallback(async (isManual = false) => {
    if (isManual) setRefreshing(true); else setLoading(true);
    setError(null);
    try {
      const [police, fire] = await Promise.all([fetchPolice(hoursFilter, divisionFilter), fetchFire()]);
      setPoliceData(police); setFireData(fire); setLastRefresh(new Date());
      const allMajors = [...police, ...fire].filter(e => e.major);
      upsertMajorEvents(allMajors, cleanType).catch(() => {});
    } catch { setError("Failed to load data. Try refreshing."); }
    finally { setLoading(false); setRefreshing(false); }
  }, [hoursFilter, divisionFilter]);

  useEffect(() => {
    if (isHistorical) { loadHistorical(); return; }
    load();
    const interval = setInterval(() => load(), 90000);
    return () => clearInterval(interval);
  }, [load, loadHistorical, isHistorical]);

  const displayData = isHistorical
    ? historicalData
    : [...policeData, ...fireData].sort((a, b) => (b.time || 0) - (a.time || 0));

  const FOOTER_H = 52;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
      {/* ── Page header bar ── */}
      <Box sx={{ height: 52, display: "flex", alignItems: "center", px: 2, gap: 1, borderBottom: `1px solid ${border}`, bgcolor: barBg, flexShrink: 0 }}>
        <LocalPoliceIcon sx={{ fontSize: 20, color: "#3b82f6", flexShrink: 0 }} />
        <Typography sx={{ fontSize: "0.85rem", fontWeight: 700, flexShrink: 0 }}>Police & Fire</Typography>
        {!isMobile && (
          <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
            {isHistorical
              ? <Chip label={`${daysFilter}d history`} size="small" icon={<HistoryIcon sx={{ fontSize: "11px !important" }} />} sx={{ fontSize: "0.7rem", height: 20, bgcolor: dark ? "#1e3a5f" : "#eff6ff", color: dark ? "#93c5fd" : "#1d4ed8", fontWeight: 600 }} />
              : <Chip label={`${hoursFilter}h live`} size="small" sx={{ fontSize: "0.7rem", height: 20, bgcolor: dark ? "#1e293b" : "#f1f5f9", color: "text.secondary", fontWeight: 600 }} />
            }
            {divisionFilter !== "All" && (
              <Chip label={DIVISION_LABELS[divisionFilter] || divisionFilter} size="small" sx={{ fontSize: "0.7rem", height: 20, bgcolor: dark ? "#1e293b" : "#f1f5f9", color: "text.secondary", fontWeight: 600 }} />
            )}
            <Chip label={viewMode === "map" ? "Map" : "List"} size="small" sx={{ fontSize: "0.7rem", height: 20, bgcolor: dark ? "#1e293b" : "#f1f5f9", color: "text.secondary", fontWeight: 600 }} />
          </Box>
        )}
        <Box sx={{ flex: 1 }} />
        {lastRefresh && (
          <Typography variant="caption" sx={{ color: "text.secondary", display: { xs: "none", sm: "block" }, fontSize: "0.72rem" }}>
            {lastRefresh.toLocaleTimeString("en-CA", { hour: "2-digit", minute: "2-digit", hour12: false })}
          </Typography>
        )}
        <Tooltip title="Refresh">
          <IconButton size="small" onClick={() => isHistorical ? loadHistorical(true) : load(true)} disabled={refreshing} sx={{ color: "text.secondary" }}>
            {refreshing ? <CircularProgress size={14} /> : <RefreshIcon sx={{ fontSize: 20 }} />}
          </IconButton>
        </Tooltip>
        <Tooltip title="Filters">
          <IconButton size="small" onClick={() => setFilterDrawerOpen(true)} sx={{ color: activeFilterCount > 0 ? "#3b82f6" : "text.secondary" }}>
            <Badge badgeContent={activeFilterCount} color="primary" sx={{ "& .MuiBadge-badge": { fontSize: "0.6rem", height: 14, minWidth: 14 } }}>
              <TuneIcon sx={{ fontSize: 20 }} />
            </Badge>
          </IconButton>
        </Tooltip>
      </Box>

      {/* ── Filter Drawer ── */}
      <SwipeableDrawer
        anchor={isMobile ? "bottom" : "right"}
        open={filterDrawerOpen}
        onOpen={() => setFilterDrawerOpen(true)}
        onClose={() => setFilterDrawerOpen(false)}
        disableSwipeToOpen
        PaperProps={{
          sx: {
            bgcolor: barBg,
            borderTop: isMobile ? `1px solid ${border}` : "none",
            borderLeft: !isMobile ? `1px solid ${border}` : "none",
            borderRadius: isMobile ? "12px 12px 0 0" : 0,
            width: isMobile ? "100%" : 280,
            maxHeight: isMobile ? "80vh" : "100vh",
          }
        }}
      >
        <Box sx={{ px: 2.5, pt: isMobile ? 2 : 2.5, pb: 1.5, display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: `1px solid ${border}` }}>
          {isMobile && <Box sx={{ width: 36, height: 4, borderRadius: 2, bgcolor: border, mx: "auto", position: "absolute", top: 8, left: "50%", transform: "translateX(-50%)" }} />}
          <Typography sx={{ fontWeight: 700, fontSize: "0.9rem" }}>Filters</Typography>
          <IconButton size="small" onClick={() => setFilterDrawerOpen(false)} sx={{ color: "text.secondary", mr: -0.5 }}>
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>
        <Box sx={{ px: 2.5, py: 2, display: "flex", flexDirection: "column", gap: 2.5, overflowY: "auto" }}>
          <Box>
            <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: "text.secondary", mb: 1 }}>View</Typography>
            <ToggleButtonGroup value={viewMode} exclusive onChange={(_, v) => v && setViewMode(v)} size="small" fullWidth>
              <ToggleButton value="list" sx={{ flex: 1 }}><ListIcon sx={{ fontSize: 14, mr: 0.5 }} />List</ToggleButton>
              <ToggleButton value="map" sx={{ flex: 1 }}><MapIcon sx={{ fontSize: 14, mr: 0.5 }} />Map</ToggleButton>
            </ToggleButtonGroup>
          </Box>
          <Divider />
          <Box>
            <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: "text.secondary", mb: 1 }}>Live window</Typography>
            <ToggleButtonGroup value={isHistorical ? null : hoursFilter} exclusive onChange={(_, v) => { if (v) { setDaysFilter(null); setHoursFilter(v); } }} size="small" fullWidth>
              {HOURS_OPTIONS.map(h => <ToggleButton key={h} value={h} sx={{ flex: 1 }}>{h}h</ToggleButton>)}
            </ToggleButtonGroup>
          </Box>
          <Box>
            <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: "text.secondary", mb: 1 }}>Historical · Major events only</Typography>
            <ToggleButtonGroup value={isHistorical ? daysFilter : null} exclusive onChange={(_, v) => { if (v) { setDaysFilter(v); setFilterDrawerOpen(false); } }} size="small" fullWidth>
              {DAYS_OPTIONS.map(d => (
                <ToggleButton key={d} value={d} sx={{ flex: 1 }}><HistoryIcon sx={{ fontSize: 11, mr: 0.4 }} />{d}d</ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>
          <Divider />
          <Box>
            <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: "text.secondary", mb: 1 }}>Division</Typography>
            <FormControl size="small" fullWidth>
              <InputLabel sx={{ fontSize: "0.78rem" }}>Division</InputLabel>
              <Select value={divisionFilter} label="Division" onChange={e => setDivisionFilter(e.target.value)} sx={{ fontSize: "0.8rem" }}>
                {POLICE_DIVISIONS.map(d => <MenuItem key={d} value={d} sx={{ fontSize: "0.8rem" }}>{d === "All" ? "All Divisions" : DIVISION_LABELS[d]}</MenuItem>)}
              </Select>
            </FormControl>
          </Box>
          <Divider />
          <Box>
            <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: "text.secondary", mb: 1 }}>Appearance</Typography>
            <ToggleButtonGroup value={colorMode} exclusive onChange={(_, v) => v && setColorMode(v)} size="small" fullWidth>
              <ToggleButton value="light" sx={{ flex: 1 }}><LightModeIcon sx={{ fontSize: 14, mr: 0.5 }} />Light</ToggleButton>
              <ToggleButton value="dark" sx={{ flex: 1 }}><DarkModeIcon sx={{ fontSize: 14, mr: 0.5 }} />Dark</ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Box>
      </SwipeableDrawer>

      {error && <Alert severity="error" sx={{ mx: 2, mt: 1, fontSize: "0.82rem" }}>{error}</Alert>}

      {/* ── Content ── */}
      <Box sx={{ flex: 1, overflow: "hidden", position: "relative" }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
            <CircularProgress size={30} />
          </Box>
        ) : viewMode === "list" ? (
          <Box sx={{ height: "100%", overflow: "auto", bgcolor: "background.default" }}>
            {displayData.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 8 }}>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>No incidents found for selected filters.</Typography>
              </Box>
            ) : isMobile ? (
              <Box>
                {displayData.map((row, idx) => (
                  <IncidentCard key={row._type === "police" ? `p-${row.id}` : `f-${row.id}`} row={row} dark={dark} index={idx}
                    onSelect={row.lat && row.lng ? () => { setSelectedIncident(row); setViewMode("map"); } : null} />
                ))}
              </Box>
            ) : (
              <TableContainer component={Paper} sx={{ height: "100%", overflow: "auto", bgcolor: "background.default", borderRadius: 0, boxShadow: "none" }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ width: 20 }} />
                      <TableCell>{isHistorical ? "Date" : "Time"}</TableCell>
                      <TableCell>Event</TableCell>
                      <TableCell>Location</TableCell>
                      <TableCell>Div.</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {displayData.map(row =>
                      row._type === "police"
                        ? <PoliceRow key={`p-${row.id}`} row={row} dark={dark} isHistorical={isHistorical} onSelect={row.lat && row.lng ? () => { setSelectedIncident(row); setViewMode("map"); } : null} />
                        : <FireRow key={`f-${row.id}`} row={row} dark={dark} isHistorical={isHistorical} onSelect={row.lat && row.lng ? () => { setSelectedIncident(row); setViewMode("map"); } : null} />
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        ) : (
          <Box sx={{ height: "100%", position: "relative" }}>
            <IncidentMap policeData={policeData} fireData={fireData} divisionFilter={divisionFilter} colorMode={colorMode}
              onDivisionClick={setDivisionFilter} historicalData={historicalData} isHistorical={isHistorical}
              selectedIncident={selectedIncident} border={border} />
            <Box sx={{ position: "absolute", bottom: 40, right: 10, zIndex: 1000, bgcolor: dark ? "#111827ee" : "#ffffffee", backdropFilter: "blur(8px)", border: `1px solid ${border}`, borderRadius: 1.5, px: 1.5, py: 1 }}>
              <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700, display: "block", mb: 0.5, textTransform: "uppercase", letterSpacing: "0.05em", fontSize: "0.65rem" }}>Legend</Typography>
              {isHistorical ? (
                <>
                  {[{ color: "#f59e0b", label: "Major (police)" }, { color: "#f97316", label: "Major (fire)" }].map(({ color, label }) => (
                    <Box key={label} sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.25 }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: color, flexShrink: 0 }} />
                      <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.72rem" }}>{label}</Typography>
                    </Box>
                  ))}
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.25 }}>
                    <Box sx={{ width: 16, height: 16, borderRadius: "50%", bgcolor: "#991b1b", border: "1.5px solid #fca5a5", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <Typography sx={{ fontSize: "0.5rem", color: "#fff", fontWeight: 800, lineHeight: 1 }}>N</Typography>
                    </Box>
                    <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.72rem" }}>Cluster (count)</Typography>
                  </Box>
                </>
              ) : (
                [{ color: "#3b82f6", label: "Police" }, { color: "#ef4444", label: "Fire" }, { color: "#f59e0b", label: "Major (police)" }, { color: "#f97316", label: "Major (fire)" }].map(({ color, label }) => (
                  <Box key={label} sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.25 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: color, flexShrink: 0 }} />
                    <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.72rem" }}>{label}</Typography>
                  </Box>
                ))
              )}
            </Box>
          </Box>
        )}
      </Box>

      {/* ── Footer ── */}
      <Box sx={{ height: FOOTER_H, bgcolor: barBg, borderTop: `1px solid ${border}`, px: 2, display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
        <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.65rem" }}>
          For entertainment only. Data: TPS & TFS. In an emergency, call 911.
        </Typography>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: isHistorical ? "#3b82f6" : "#10b981", animation: "pulse 2s infinite" }} />
          <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.65rem" }}>
            {isHistorical ? `Historical · ${daysFilter}d` : "Live · 90s"}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
